import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { Home, Calendar, Plus, Trash2, Send, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';

const AirbnbInvoice = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  // Generate month options
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = -3; i <= 2; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const year = date.getFullYear();
      const month = date.getMonth();
      const monthName = date.toLocaleString('en-GB', { month: 'long' });
      const lastDay = new Date(year, month + 1, 0).getDate();
      options.push({
        label: `${monthName} ${year}`,
        value: `${year}-${String(month + 1).padStart(2, '0')}`,
        start: `${year}-${String(month + 1).padStart(2, '0')}-01`,
        end: `${year}-${String(month + 1).padStart(2, '0')}-${lastDay}`,
        monthName,
        year
      });
    }
    return options;
  };

  const monthOptions = getMonthOptions();
  const currentMonthValue = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const currentMonth = monthOptions.find(m => m.value === currentMonthValue);

  const [form, setForm] = useState({
    site_id: '',
    selected_month: currentMonthValue,
    period_start: currentMonth?.start || '',
    period_end: currentMonth?.end || '',
    work_days: '',
    bookings: [],
    extras: [],
    discounts: []
  });

  const [newBooking, setNewBooking] = useState({ description: '', quantity: '', unit_price: '' });
  const [newExtra, setNewExtra] = useState({ description: '', amount: '' });
  const [newDiscount, setNewDiscount] = useState({ description: '', amount: '' });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      // Fetch sites with their client info
      const [sitesRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/sites`),
        fetch(`${API_URL}/api/admin/clients`)
      ]);
      const sitesData = await sitesRes.json();
      const clientsData = await clientsRes.json();
      
      // Merge client info into sites
      const sitesWithClients = (sitesData.sites || []).map(site => {
        const client = (clientsData.clients || []).find(c => c.id === site.client_id);
        return {
          ...site,
          client_name: client?.company_name || 'Unknown Client'
        };
      });
      
      setSites(sitesWithClients);
    } catch (err) {
      console.error('Failed to fetch sites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (monthValue) => {
    const month = monthOptions.find(m => m.value === monthValue);
    if (month) {
      setForm(prev => ({
        ...prev,
        selected_month: monthValue,
        period_start: month.start,
        period_end: month.end
      }));
    }
  };

  const addBooking = () => {
    if (newBooking.description && newBooking.quantity && newBooking.unit_price) {
      setForm(prev => ({
        ...prev,
        bookings: [...prev.bookings, {
          description: newBooking.description,
          quantity: parseInt(newBooking.quantity),
          unit_price: parseFloat(newBooking.unit_price)
        }]
      }));
      setNewBooking({ description: '', quantity: '', unit_price: '' });
    }
  };

  const removeBooking = (index) => {
    setForm(prev => ({
      ...prev,
      bookings: prev.bookings.filter((_, i) => i !== index)
    }));
  };

  const addExtra = () => {
    if (newExtra.description && newExtra.amount) {
      setForm(prev => ({
        ...prev,
        extras: [...prev.extras, {
          description: newExtra.description,
          amount: parseFloat(newExtra.amount)
        }]
      }));
      setNewExtra({ description: '', amount: '' });
    }
  };

  const removeExtra = (index) => {
    setForm(prev => ({
      ...prev,
      extras: prev.extras.filter((_, i) => i !== index)
    }));
  };

  const addDiscount = () => {
    if (newDiscount.description && newDiscount.amount) {
      setForm(prev => ({
        ...prev,
        discounts: [...prev.discounts, {
          description: newDiscount.description,
          amount: parseFloat(newDiscount.amount)
        }]
      }));
      setNewDiscount({ description: '', amount: '' });
    }
  };

  const removeDiscount = (index) => {
    setForm(prev => ({
      ...prev,
      discounts: prev.discounts.filter((_, i) => i !== index)
    }));
  };

  const formatWorkDays = (days) => {
    if (!days) return '';
    const month = monthOptions.find(m => m.value === form.selected_month);
    return days.split(',').map(d => {
      const num = parseInt(d.trim());
      if (isNaN(num)) return '';
      const suffix = num === 1 || num === 21 || num === 31 ? 'st' : num === 2 || num === 22 ? 'nd' : num === 3 || num === 23 ? 'rd' : 'th';
      return `${num}${suffix}`;
    }).filter(d => d).join(', ') + (month ? ` ${month.monthName} ${month.year}` : '');
  };

  const calculateTotal = () => {
    let total = 0;
    
    // Bookings
    form.bookings.forEach(b => {
      total += b.quantity * b.unit_price;
    });
    
    // Extras
    form.extras.forEach(e => {
      total += e.amount;
    });
    
    // Discounts
    form.discounts.forEach(d => {
      total -= d.amount;
    });
    
    return total;
  };

  const handleGenerate = async () => {
    if (!form.site_id) {
      alert('Please select a property');
      return;
    }
    if (form.bookings.length === 0) {
      alert('Please add at least one booking');
      return;
    }

    setGenerating(true);
    setSuccess(null);

    try {
      const res = await fetch(`${API_URL}/api/admin/invoices/airbnb`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: form.site_id,
          period_start: form.period_start,
          period_end: form.period_end,
          work_days: form.work_days,
          bookings: form.bookings,
          extras: form.extras,
          discounts: form.discounts
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        setSuccess({
          invoice_number: data.invoice.invoice_number,
          total: data.invoice.total_amount,
          id: data.invoice.id
        });
        // Reset form
        setForm(prev => ({
          ...prev,
          bookings: [],
          extras: [],
          discounts: [],
          work_days: ''
        }));
      } else {
        alert(data.detail || 'Failed to generate invoice');
      }
    } catch (err) {
      console.error('Failed to generate invoice:', err);
      alert('Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const selectedSite = sites.find(s => s.id === form.site_id);

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
            <Home className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">AirBnB Invoice</h1>
            <p className="text-gray-500 text-sm">Generate invoice for short-term rental cleans</p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
          <p className="text-green-800 font-medium">Invoice {success.invoice_number} created!</p>
          <p className="text-green-600 text-sm">Total: £{success.total.toFixed(2)}</p>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={() => navigate(`/admin/invoices/${success.id}`)} className="bg-green-600 hover:bg-green-700">
              View Invoice
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSuccess(null)}>
              Create Another
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
        {/* Property Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Property</label>
          <select
            value={form.site_id}
            onChange={(e) => setForm({ ...form, site_id: e.target.value })}
            className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none bg-white text-base"
            data-testid="airbnb-property-select"
          >
            <option value="">Select property...</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.site_name} - {site.client_name}
              </option>
            ))}
          </select>
          {sites.length === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              No properties found. <a href="/admin/clients" className="text-rose-600 hover:underline">Add a client and site first</a>
            </p>
          )}
        </div>

        {/* Month Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-1" />
            Invoice Month
          </label>
          <select
            value={form.selected_month}
            onChange={(e) => handleMonthChange(e.target.value)}
            className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none bg-white text-lg font-medium"
            data-testid="airbnb-month-select"
          >
            {monthOptions.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </div>

        {/* Work Days */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Cleaning Dates</label>
          <input
            type="text"
            placeholder="e.g., 5, 12, 19, 26"
            value={form.work_days}
            onChange={(e) => setForm({ ...form, work_days: e.target.value })}
            className="w-full h-12 px-4 rounded-lg border border-gray-200 focus:border-rose-500 focus:ring-2 focus:ring-rose-100 outline-none text-base"
            data-testid="airbnb-work-days"
          />
          {form.work_days && (
            <p className="text-sm text-rose-600 mt-1">
              → {formatWorkDays(form.work_days)}
            </p>
          )}
        </div>

        {/* Bookings Section */}
        <div className="border-t border-gray-100 pt-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Bookings / Cleans</label>
          
          {form.bookings.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.bookings.map((booking, index) => (
                <div key={index} className="flex items-center gap-3 bg-rose-50 rounded-lg px-4 py-3">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{booking.description}</p>
                    <p className="text-sm text-gray-500">{booking.quantity} × £{booking.unit_price.toFixed(2)}</p>
                  </div>
                  <p className="font-semibold text-rose-600">£{(booking.quantity * booking.unit_price).toFixed(2)}</p>
                  <button onClick={() => removeBooking(index)} className="text-gray-400 hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-12 gap-2">
            <input
              type="text"
              placeholder="Clean type (e.g., Standard, 5 beds or less)"
              value={newBooking.description}
              onChange={(e) => setNewBooking({ ...newBooking, description: e.target.value })}
              className="col-span-5 h-11 px-3 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm"
            />
            <input
              type="number"
              min="1"
              placeholder="Qty"
              value={newBooking.quantity}
              onChange={(e) => setNewBooking({ ...newBooking, quantity: e.target.value })}
              className="col-span-2 h-11 px-3 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm text-center"
            />
            <div className="col-span-3 relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="Price"
                value={newBooking.unit_price}
                onChange={(e) => setNewBooking({ ...newBooking, unit_price: e.target.value })}
                className="w-full h-11 pl-7 pr-3 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm"
              />
            </div>
            <Button 
              type="button" 
              onClick={addBooking} 
              disabled={!newBooking.description || !newBooking.quantity || !newBooking.unit_price}
              className="col-span-2 h-11 bg-rose-600 hover:bg-rose-700"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Add each type of clean with quantity and price per clean</p>
        </div>

        {/* Extras Section */}
        <div className="border-t border-gray-100 pt-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Extras</label>
          
          {form.extras.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.extras.map((extra, index) => (
                <div key={index} className="flex items-center gap-3 bg-green-50 rounded-lg px-4 py-2">
                  <span className="flex-1 text-sm">{extra.description}</span>
                  <span className="font-medium text-green-700">+£{extra.amount.toFixed(2)}</span>
                  <button onClick={() => removeExtra(index)} className="text-gray-400 hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Extra description (e.g., Linen change)"
              value={newExtra.description}
              onChange={(e) => setNewExtra({ ...newExtra, description: e.target.value })}
              className="flex-1 h-10 px-3 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm"
            />
            <div className="relative w-24">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={newExtra.amount}
                onChange={(e) => setNewExtra({ ...newExtra, amount: e.target.value })}
                className="w-full h-10 pl-7 pr-2 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm"
              />
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addExtra} disabled={!newExtra.description || !newExtra.amount}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Discounts Section */}
        <div className="border-t border-gray-100 pt-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Discounts</label>
          
          {form.discounts.length > 0 && (
            <div className="space-y-2 mb-4">
              {form.discounts.map((discount, index) => (
                <div key={index} className="flex items-center gap-3 bg-orange-50 rounded-lg px-4 py-2">
                  <span className="flex-1 text-sm">{discount.description}</span>
                  <span className="font-medium text-orange-700">-£{discount.amount.toFixed(2)}</span>
                  <button onClick={() => removeDiscount(index)} className="text-gray-400 hover:text-red-500 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Discount reason"
              value={newDiscount.description}
              onChange={(e) => setNewDiscount({ ...newDiscount, description: e.target.value })}
              className="flex-1 h-10 px-3 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm"
            />
            <div className="relative w-24">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">£</span>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={newDiscount.amount}
                onChange={(e) => setNewDiscount({ ...newDiscount, amount: e.target.value })}
                className="w-full h-10 pl-7 pr-2 rounded-lg border border-gray-200 focus:border-rose-500 outline-none text-sm"
              />
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addDiscount} disabled={!newDiscount.description || !newDiscount.amount}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Total */}
        <div className="border-t border-gray-100 pt-6">
          <div className="bg-gray-900 rounded-xl p-5 text-white">
            <div className="flex justify-between items-center">
              <span className="text-gray-300">Total Due</span>
              <span className="text-3xl font-bold">£{calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <Button 
          onClick={handleGenerate} 
          disabled={generating || !form.site_id || form.bookings.length === 0}
          className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-lg"
          data-testid="airbnb-generate-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Generate Invoice
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AirbnbInvoice;
